/**
 * Canonical extinguisher type vocabulary shared by every add/edit form.
 *
 * Values are the fire-class codes the API accepts (`src/api/extinguishers.ts`).
 * Labels describe the common agent so a facilities manager can match what is
 * printed on the cylinder without knowing the class code.
 */
export const EXTINGUISHER_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ABC', label: 'ABC dry chemical (multipurpose, most common)' },
  { value: 'A', label: 'Class A - water or foam (ordinary combustibles)' },
  { value: 'B', label: 'Class B - CO2 or dry chemical (flammable liquids)' },
  { value: 'C', label: 'Class C - CO2 or clean agent (electrical)' },
  { value: 'D', label: 'Class D - combustible metals' },
  { value: 'K', label: 'Class K - wet chemical (commercial kitchens)' },
];

export const EXTINGUISHER_TYPE_VALUES = EXTINGUISHER_TYPES.map((t) => t.value);

export function extinguisherTypeLabel(value: string): string {
  return EXTINGUISHER_TYPES.find((t) => t.value === value)?.label ?? value;
}

/**
 * The extinguishing agent, which is a different fact from the fire class above.
 * A CO2 unit rated BC has no gauge and is checked by weight; a dry-chemical BC
 * unit has a gauge. A record holding only "B" cannot tell an inspector which
 * one they are looking at, or how it should have been checked.
 *
 * Values match the canonical set the API accepts (`KNOWN_AGENTS` in
 * `src/services/import.ts`).
 */
export const EXTINGUISHER_AGENTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Not recorded' },
  { value: 'Dry chemical', label: 'Dry chemical (has a gauge)' },
  { value: 'CO2', label: 'CO2 (no gauge \u2014 checked by weight)' },
  { value: 'Wet chemical', label: 'Wet chemical (kitchen)' },
  { value: 'Water', label: 'Water' },
  { value: 'Water mist', label: 'Water mist' },
  { value: 'Foam', label: 'Foam / AFFF' },
  { value: 'Clean agent', label: 'Clean agent' },
  { value: 'Halotron', label: 'Halotron' },
  { value: 'Halon', label: 'Halon' },
  { value: 'FE-36', label: 'FE-36' },
  { value: 'Dry powder', label: 'Dry powder (combustible metals)' },
];

/** The agent most units of a given class carry, offered as the starting point. */
export const DEFAULT_AGENT_FOR_CLASS: Record<string, string> = {
  ABC: 'Dry chemical',
  A: 'Water',
  B: 'CO2',
  C: 'CO2',
  D: 'Dry powder',
  K: 'Wet chemical',
};
